import { useEffect, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FaArrowLeftLong } from "react-icons/fa6";
import {
  resetPassword,
  setOtpSuccess,
  showToast,
  useAppDispatch,
  useAppSelector,
  getOtpButtonVisibility,
} from "@hypexone/uicore";
import Button from "../../components/shared/Button/Button";
import { useSignUpModal } from "../../context/SignUpModalContext";
import { SVLS_API_CLIENT } from "../../../App.utils";

interface ForgotPasswordModalProps {
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ onClose }) => {
  const [method, setMethod] = useState("Username");
  const [username, setUsername] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpTimer, setOtpTimer] = useState<number | undefined>(undefined);

  const { otpSent, otpSuccess } = useAppSelector((state) => state.auth);
  const { langData } = useAppSelector((state) => state.cms);
  const { domainConfig } = useAppSelector((state) => state.common);
  const dispatch = useAppDispatch();
  const { setSignUpModalOpen } = useSignUpModal();

  const handleOtpTimer = (time: number) => {
    if (time >= 0) {
      setOtpTimer(time);
      setTimeout(() => handleOtpTimer(time - 1), 1000);
    } else {
      setTimeout(() => setOtpTimer(undefined), 1000);
    }
  };

  useEffect(() => {
    if (otpSent) handleOtpTimer(60);
  }, [otpSent]);

  useEffect(() => {
    if (otpSuccess) handleOtpTimer(60);
  }, [otpSuccess]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleMethodChange = (newMethod: string) => {
    setMethod(newMethod);
    setUsername("");
    setMobile("");
  };

  const handlesendotp = async (isWhatsapp = false) => {
    try {
      const userIdentifier =
        method === "Username" ? username.toLowerCase() : "91" + mobile;
      const queryParam =
        method === "Username"
          ? isWhatsapp
            ? "?isWhatsapp=true"
            : ""
          : `?userIdentifierType=PHONE_NUMBER${
              isWhatsapp ? "&isWhatsapp=true" : ""
            }`;
      const url = `/account/v2/users/${encodeURIComponent(
        userIdentifier
      )}/:sendResetPasswordOtp${queryParam}`;
      const response = await SVLS_API_CLIENT.post(url);
      if (response.status === 204) {
        dispatch(
          showToast({
            message: "OTP sent successfully.",
            type: "success",
          })
        );
        dispatch(setOtpSuccess(true));
        handleOtpTimer(60);
      } else {
        dispatch(
          showToast({
            message: "Unable to send OTP.",
            type: "error",
          })
        );
      }
    } catch (err: any) {
      dispatch(
        showToast({
          message:
            err?.response?.data?.message ||
            langData?.["something_went_wrong"] ||
            "Unable to send OTP.",
          type: "error",
        })
      );
    }
  };

  const { showSmsOtpButton, showWhatsappOtpButton, showGenericOtpButton } =
    getOtpButtonVisibility(domainConfig);
  const showOtpButtons =
    showSmsOtpButton || showWhatsappOtpButton || showGenericOtpButton;
  const isOtpDisabled =
    method === "mobile" ? mobile.length < 10 : username.length === 0;

  const redirectToSignUp = () => {
    onClose();
    setSignUpModalOpen(true);
  };

  const handleResetPassword = () => {
    if (newPassword !== confirmPassword) {
      dispatch(
        showToast({
          message: "Passwords didn't match",
          type: "error",
        })
      );
      return;
    }
    dispatch(
      resetPassword({
        username,
        phoneNumber: "91" + mobile,
        resetPassword: newPassword,
        otp,
        option: method,
      })
    );
  };

  const primaryBtn =
    "h-10 rounded-xl bg-gradient-to-b from-[#2f2f2f] via-black to-[#141414] px-3 text-[13px] font-semibold text-goldenYellow shadow-[0_6px_16px_rgba(0,0,0,0.18)] transition-all hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60";
  const inputClass =
    "h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-black shadow-sm outline-none placeholder:text-neutral-400 focus:border-black/30";

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto bg-[#1a1a1a] p-3 sm:p-5"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative my-auto w-full max-w-md overflow-hidden rounded-2xl border border-black/20 bg-goldenYellow shadow-[0_16px_48px_rgba(0,0,0,0.45)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="forgot-password-modal-title"
      >
        <div className="relative px-4 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1.5 rounded-full bg-black/95 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg transition-colors hover:bg-black"
            >
              <FaArrowLeftLong className="h-3.5 w-3.5" />
              <span>{langData?.["back"] ?? "Back"}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/95 text-white shadow-lg"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <h2
            id="forgot-password-modal-title"
            className="mb-1 text-center text-xl font-bold text-black3"
          >
            {langData?.["forgot_password"]}
          </h2>
          <p className="mb-4 text-center text-xs text-black4">
            {langData?.["otp_send_msg"] ??
              "Enter your username or mobile to receive a reset OTP."}
          </p>

          {!otpSuccess ? (
            <>
              <div className="mb-3 grid grid-cols-2 gap-1 rounded-xl bg-black/10 p-1">
                <button
                  type="button"
                  className={`rounded-lg py-2 text-sm font-semibold transition-all ${
                    method === "Username"
                      ? "bg-black text-white shadow-sm"
                      : "bg-transparent text-black4"
                  }`}
                  onClick={() => handleMethodChange("Username")}
                >
                  {langData?.["username"] ?? "Username"}
                </button>
                <button
                  type="button"
                  className={`rounded-lg py-2 text-sm font-semibold transition-all ${
                    method === "mobile"
                      ? "bg-black text-white shadow-sm"
                      : "bg-transparent text-black4"
                  }`}
                  onClick={() => handleMethodChange("mobile")}
                >
                  Mobile Number
                </button>
              </div>

              {method === "Username" ? (
                <div className="mb-3">
                  <input
                    type="text"
                    className={inputClass}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={langData?.["enter_username_txt"]}
                  />
                </div>
              ) : (
                <div className="mb-3">
                  <div className="flex overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                    <select
                      className="h-10 w-[72px] border-0 border-r border-neutral-200 bg-transparent px-2 text-sm outline-none"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                    >
                      <option value="+91">+91</option>
                    </select>
                    <input
                      type="text"
                      className="h-10 w-full border-0 bg-transparent px-3 text-sm outline-none placeholder:text-neutral-400"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder={langData?.["enter_mobile_no"]}
                    />
                  </div>
                </div>
              )}

              {otpTimer !== undefined && otpTimer !== null && otpTimer >= 0 ? (
                <Button
                  className="h-10 w-full cursor-not-allowed rounded-xl bg-neutral-400 py-2 text-sm text-white"
                  disabled
                >
                  {`Resend in ${otpTimer} sec`}
                </Button>
              ) : showOtpButtons ? (
                <div className="flex gap-2">
                  {(showSmsOtpButton || showGenericOtpButton) && (
                    <Button
                      className={`${primaryBtn} flex-1 ${!showWhatsappOtpButton ? "w-full" : ""}`}
                      disabled={isOtpDisabled}
                      onClick={() => handlesendotp(false)}
                    >
                      {showGenericOtpButton
                        ? langData?.["send_otp"] ?? "Send OTP"
                        : langData?.["get_otp"] ?? "Get OTP"}
                    </Button>
                  )}
                  {showWhatsappOtpButton && (
                    <Button
                      className={`h-10 flex-1 rounded-xl bg-[#25D366] px-3 text-[13px] font-semibold text-white shadow-sm transition-all hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60 ${
                        !showSmsOtpButton && !showGenericOtpButton ? "w-full" : ""
                      }`}
                      disabled={isOtpDisabled}
                      onClick={() => handlesendotp(true)}
                    >
                      {langData?.["get_otp_whatsapp"] ?? "WhatsApp OTP"}
                    </Button>
                  )}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="mb-3">
                <label className="mb-1 block text-xs font-medium text-black4">
                  {langData?.["otp"]}
                </label>
                <input
                  type="number"
                  className={`${inputClass} appearance-none`}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder={langData?.["enter_otp_txt"]}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="fp-password" className="mb-1 block text-xs font-medium text-black4">
                  {langData?.["password"]}
                </label>
                <div className="flex h-10 items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 shadow-sm">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="fp-password"
                    className="w-full border-none bg-transparent text-sm outline-none"
                    placeholder={langData?.["enter_your_password"]}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0 text-neutral-500"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
              </div>
              <div className="mb-3">
                <label
                  htmlFor="fp-confirm-password"
                  className="mb-1 block text-xs font-medium text-black4"
                >
                  {langData?.["confirm_password"]}
                </label>
                <div className="flex h-10 items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 shadow-sm">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="fp-confirm-password"
                    className="w-full border-none bg-transparent text-sm outline-none"
                    placeholder={langData?.["confirm_your_password"]}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0 text-neutral-500"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label="Toggle confirm password visibility"
                  >
                    {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
              </div>

              {otpTimer !== undefined && otpTimer !== null && otpTimer >= 0 ? (
                <Button
                  className="mb-2 h-10 w-full cursor-not-allowed rounded-xl bg-neutral-400 py-2 text-sm text-white"
                  disabled
                >
                  {`Resend in ${otpTimer} sec`}
                </Button>
              ) : showOtpButtons ? (
                <div className="mb-2 flex gap-2">
                  {(showSmsOtpButton || showGenericOtpButton) && (
                    <Button
                      className={`${primaryBtn} flex-1 ${!showWhatsappOtpButton ? "w-full" : ""}`}
                      onClick={() => handlesendotp(false)}
                    >
                      {showGenericOtpButton
                        ? langData?.["send_otp"] ?? "Send OTP"
                        : langData?.["resend_sms_otp"] ?? "Resend SMS OTP"}
                    </Button>
                  )}
                  {showWhatsappOtpButton && (
                    <Button
                      className={`h-10 flex-1 rounded-xl bg-[#25D366] px-3 text-[13px] font-semibold text-white ${
                        !showSmsOtpButton && !showGenericOtpButton ? "w-full" : ""
                      }`}
                      onClick={() => handlesendotp(true)}
                    >
                      {langData?.["resend_whatsapp_otp"] ?? "Resend WhatsApp OTP"}
                    </Button>
                  )}
                </div>
              ) : null}

              <Button className={`${primaryBtn} w-full`} onClick={handleResetPassword}>
                {langData?.["reset_password_txt"]}
              </Button>
            </>
          )}

          <p className="mt-4 text-center text-xs text-black4">
            {langData?.["account_not_found_txt"] ?? "Don't have account?"}{" "}
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent p-0 font-bold text-black underline underline-offset-2"
              onClick={redirectToSignUp}
            >
              {langData?.["sign_up"]}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
